import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { WeatherStationNearestModel } from '../../models/weather-station-nearest';
import { WeatherStationNearestAdapter } from '../../adapters/weather-station-nearest';
import { WeatherStationLastDaysModel } from '../../models/weather-station-last-days';
import { WeatherStationLastDaysAdapter } from '../../adapters/weather-station-last-days';
import { WeatherStationLastDaysByCityModel } from '../../models/weather-station-last-days-by-city';
import { WeatherStationLastDaysByCityAdapter } from '../../adapters/weather-station-last-days-by-city';
import { WeatherStationRealTimeModel } from '../../models/weather-station-real-time';
import { WeatherStationRealTimeAdapter } from '../../adapters/weather-station-real-time';
import { WeatherStationRealTimeByCityModel } from '../../models/weather-station-real-time-by-city';
import { WeatherStationRealTimeByCityAdapter } from '../../adapters/weather-station-real-time-by-city';
import { WeatherStationDailyAverageModel } from '../../models/weather-station-daily-average';
import { WeatherStationDailyAverageAdapter } from '../../adapters/weather-station-daily-average';
import { CityWithActiveStationModel } from '../../models/cities-stations-active';
import { CitiesWithActiveStationsAdapter } from '../../adapters/cities-stations-active';
import { ApiBaseService } from '../../../../core/services/api/api-base.service';

@Injectable({
  providedIn: 'root',
})
export class WeatherConditionsApiService extends ApiBaseService {
  constructor(http: HttpClient) {
    super(http);
  }

  // Buscar estação mais próxima com base em latitude e longitude
  getNearestStation(
    latitude: number,
    longitude: number,
  ): Observable<WeatherStationNearestModel> {
    // Backend valida formato: latitude (2 dígitos.7 decimais) e longitude (3 dígitos.7 decimais)
    const params = {
      latitude: Number(latitude.toFixed(7)),
      longitude: Number(longitude.toFixed(7)),
    };

    return this.http
      .get<any>(`${this.apiBaseUrl}/weather-station/stations/nearest`, {
        params,
      })
      .pipe(map((response) => WeatherStationNearestAdapter.fromApi(response)));
  }

  // Buscar dados dos últimos N dias de uma estação
  getLastDays(
    stationId: number,
    numDays: number = 5,
  ): Observable<WeatherStationLastDaysModel[]> {
    return this.http
      .get<any>(
        `${this.apiBaseUrl}/weather-station/data/last-days/${stationId}`,
        {
          params: {
            num_days: numDays.toString(),
          },
        },
      )
      .pipe(
        map((response) => WeatherStationLastDaysAdapter.fromApiArray(response)),
      );
  }

  // Buscar dados em tempo real de uma estação
  getRealTimeData(stationId: number): Observable<WeatherStationRealTimeModel> {
    return this.http
      .get<any>(
        `${this.apiBaseUrl}/weather-station/data/real-time/${stationId}`,
      )
      .pipe(map((response) => WeatherStationRealTimeAdapter.fromApi(response)));
  }

  // Buscar dados em tempo real por cidade
  getRealTimeDataByCity(
    cityId: number,
  ): Observable<WeatherStationRealTimeByCityModel | null> {
    return this.http
      .get<any>(
        `${this.apiBaseUrl}/weather-station/data/real-time/by-city/${cityId}`,
      )
      .pipe(
        map((response) =>
          WeatherStationRealTimeByCityAdapter.fromApi(response),
        ),
      );
  }

  // Buscar média diária de temperatura de uma estação
  getDailyAverage(
    stationId: number,
    date?: string,
  ): Observable<WeatherStationDailyAverageModel> {
    const options = date ? { params: { date } } : {};
    return this.http
      .get<any>(
        `${this.apiBaseUrl}/weather-station/data/hourly/daily-average/${stationId}`,
        options,
      )
      .pipe(
        map((response) => WeatherStationDailyAverageAdapter.fromApi(response)),
      );
  }

  // Buscar cidades com estações ativas
  getCitiesWithActiveStations(): Observable<CityWithActiveStationModel[]> {
    return this.http
      .get<any>(`${this.apiBaseUrl}/location/cities/stations-active`)
      .pipe(
        map((response) => CitiesWithActiveStationsAdapter.fromApi(response)),
      );
  }

  // Buscar dados dos últimos N dias de uma cidade
  getLastDaysByCity(
    cityId: number,
    numDays: number = 5,
  ): Observable<WeatherStationLastDaysByCityModel[]> {
    return this.http
      .get<any>(
        `${this.apiBaseUrl}/weather-station/data/last-days/by-city/${cityId}`,
        {
          params: {
            num_days: numDays.toString(),
          },
        },
      )
      .pipe(
        map((response) =>
          WeatherStationLastDaysByCityAdapter.fromApiArray(response),
        ),
      );
  }
}
